/**
 * Eigenbau: Metarr-Konfiguration in den Einstellungen.
 *
 * Nur Betriebsfunktionen: Anbieterverbindungen, Modellrouting und Hausmeister.
 * Anliegen, Entwürfe und Vorschläge leben auf der Metarr-Übersicht (`/metarr`).
 */

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ExternalLink, KeyRound, Plus, RefreshCw, Trash2 } from "lucide-react";
import { Button } from "@/components/Button";
import { Badge } from "@/components/Badge";
import { Input } from "@/components/Input";
import { Label } from "@/components/Label";
import { Fehlerzeile } from "@/components/Fehlerzeile";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/Dialog";
import { KiAnbieterLogo } from "@/components/KiAnbieterLogo";
import { ModellAuswahl } from "@/components/ModellAuswahl";
import { HausmeisterAbschnitt } from "@/seiten/HausmeisterAbschnitt";
import { api } from "@/lib/api";
import { fehlerMeldung } from "@/lib/haken";
import type { Meldung } from "@/lib/meldung";
import {
  EinstellungenHuelle,
  EinstellungsAbschnitt,
  LadenZeile,
} from "@/seiten/einstellungen/gemeinsam";
import {
  einstellbareFelderFuerUmgebung,
  listeKiModellCapabilities,
  type ReasoningStufe,
} from "@cubic/shared";

type ModellVerwendung =
  | "metarr_anliegen"
  | "akzeptanzkriterien"
  | "review"
  | "hausmeister";

type ModellAuswahl = { verbindungId: string; modell: string };
type ModellEntwurf = { zuordnung: Record<ModellVerwendung, ModellAuswahl> };
type AnbieterArt =
  | "deepseek_api"
  | "openai_api"
  | "openai_subscription"
  | "xai_api"
  | "xai_subscription"
  | "anthropic_subscription";

const modellVerwendungen: Array<{
  id: ModellVerwendung;
  label: string;
  beschreibung: string;
}> = [
  {
    id: "metarr_anliegen",
    label: "Allgemeine Metarr-Anliegen",
    beschreibung: "Planen und Vorschläge für frei formulierte Anliegen.",
  },
  {
    id: "akzeptanzkriterien",
    label: "Akzeptanzkriterien",
    beschreibung: "Prüfbare Kriterien aus Aufgaben und Kommentaren ableiten.",
  },
  {
    id: "review",
    label: "Review",
    beschreibung: "Code- und Inhaltsänderungen gründlich untersuchen.",
  },
  {
    id: "hausmeister",
    label: "Hausmeister",
    beschreibung: "Regelmäßige Hintergrund- und Konsistenzprüfungen.",
  },
];

const modellkennungMuster = /^[a-zA-Z0-9._:/-]{1,200}$/;

/*
 * Dieselben Stufen wie im Adapter (`DENKSTUFEN` in
 * `openai-kompatibler-adapter-service.ts`). Bewusst wiederholt statt
 * importiert: Die Oberfläche zieht nichts aus `apps/server` — geteilt wird
 * über `packages/shared`, und für vier Wörter lohnt der Umweg nicht. Der
 * Server prüft den Wert ohnehin, per Zod und per Check-Constraint.
 */
const DENKSTUFEN = ["minimal", "low", "medium", "high"] as const;

export function EinstellungenMetarr() {
  const client = useQueryClient();
  const [meldung, setzeMeldung] = useState<Meldung | null>(null);
  const [modellEntwurf, setzeModellEntwurf] =
    useState<ModellEntwurf | null>(null);
  const [modellGespeichert, setzeModellGespeichert] = useState(false);
  const [anbieterDialogOffen, setzeAnbieterDialogOffen] = useState(false);
  const [anbieterArt, setzeAnbieterArt] = useState<AnbieterArt | null>(null);
  const [verbindungsName, setzeVerbindungsName] = useState("");
  const [bestaetigeEntfernen, setzeBestaetigeEntfernen] = useState<{
    verbindungId: string;
    name: string;
  } | null>(null);
  const [apiKey, setzeApiKey] = useState("");

  const anbieterVerbindungen = useQuery({
    queryKey: ["cubic", "metarr", "anbieter-verbindungen"],
    queryFn: () => api.metarr.anbieterVerbindungen.query(),
  });

  const modellKonfiguration = useQuery({
    queryKey: ["cubic", "metarr", "modell-konfiguration"],
    queryFn: () => api.metarr.modellKonfiguration.query(),
  });

  const anbieterVerknuepfen = useMutation({
    mutationFn: () =>
      api.metarr.anbieterVerknuepfen.mutate({
        anbieterArt: anbieterArt!,
        name: verbindungsName.trim(),
        apiKey: apiKey.trim(),
      }),
    onSuccess: async () => {
      setzeAnbieterDialogOffen(false);
      setzeAnbieterArt(null);
      setzeVerbindungsName("");
      setzeApiKey("");
      setzeMeldung(null);
      await client.invalidateQueries({
        queryKey: ["cubic", "metarr", "anbieter-verbindungen"],
      });
      await client.invalidateQueries({
        queryKey: ["cubic", "metarr", "modell-konfiguration"],
      });
    },
    onError: (e) => setzeMeldung(fehlerMeldung(e)),
  });

  const anbieterTesten = useMutation({
    mutationFn: (verbindungId: string) =>
      api.metarr.anbieterVerbindungTesten.mutate({ verbindungId }),
    onSuccess: async () => {
      setzeMeldung(null);
      await client.invalidateQueries({
        queryKey: ["cubic", "metarr", "anbieter-verbindungen"],
      });
    },
    onError: (e) => setzeMeldung(fehlerMeldung(e)),
  });

  /*
   * Denkmodus und Denkstufe getrennt schaltbar, aber über denselben Aufruf:
   * Weggelassene Felder bleiben unverändert, `null` setzt auf die Vorgabe
   * zurück. So ändert ein Klick auf „Denken" nicht ungefragt die Stufe.
   */
  const denkmodus = useMutation({
    mutationFn: (eingabe: {
      verbindungId: string;
      denken?: boolean;
      denkstufe?: (typeof DENKSTUFEN)[number] | null;
      maxTokens?: number | null;
    }) => api.metarr.anbieterVerbindungDenkmodus.mutate(eingabe),
    onSuccess: async () => {
      setzeMeldung(null);
      await client.invalidateQueries({
        queryKey: ["cubic", "metarr", "anbieter-verbindungen"],
      });
    },
    onError: (e) => setzeMeldung(fehlerMeldung(e)),
  });

  /*
    Zugänge aus der Serverkonfiguration gehen einen eigenen Weg: Ihre Werte
    liegen in `ki_umgebungs_einstellungen`, und ihre Stufenleiter ist die der
    Modellfähigkeiten, nicht `DENKSTUFEN`.
  */
  const umgebungsDenkmodus = useMutation({
    mutationFn: (eingabe: {
      verbindungId: string;
      denken?: boolean;
      denkstufe?: ReasoningStufe | null;
      maxTokens?: number | null;
    }) => api.metarr.umgebungsVerbindungDenkmodus.mutate(eingabe),
    onSuccess: async () => {
      setzeMeldung(null);
      await client.invalidateQueries({
        queryKey: ["cubic", "metarr", "anbieter-verbindungen"],
      });
    },
    onError: (e) => setzeMeldung(fehlerMeldung(e)),
  });

  const anbieterEntfernen = useMutation({
    mutationFn: (verbindungId: string) =>
      api.metarr.anbieterVerbindungEntfernen.mutate({ verbindungId }),
    onSuccess: async () => {
      setzeModellEntwurf(null);
      setzeMeldung(null);
      await Promise.all([
        client.invalidateQueries({
          queryKey: ["cubic", "metarr", "anbieter-verbindungen"],
        }),
        client.invalidateQueries({
          queryKey: ["cubic", "metarr", "modell-konfiguration"],
        }),
      ]);
    },
    onError: (e) => setzeMeldung(fehlerMeldung(e)),
  });

  const modellKonfigurationSpeichern = useMutation({
    mutationFn: (eingabe: ModellEntwurf) =>
      api.metarr.modellKonfigurationSpeichern.mutate(eingabe),
    onSuccess: async (daten) => {
      const werte = daten.zuordnung;
      if (Object.values(werte).every((wert) => wert !== null)) {
        setzeModellEntwurf({
          zuordnung: werte as Record<ModellVerwendung, ModellAuswahl>,
        });
      }
      setzeModellGespeichert(true);
      setzeMeldung(null);
      await client.invalidateQueries({
        queryKey: ["cubic", "metarr", "modell-konfiguration"],
      });
    },
    onError: (e) => {
      setzeModellGespeichert(false);
      setzeMeldung(fehlerMeldung(e));
    },
  });

  const ersteVerbindung = anbieterVerbindungen.data?.verbindungen[0];
  const modellWerte =
    modellEntwurf ??
    (() => {
      if (!modellKonfiguration.data || !ersteVerbindung) return null;
      const zuordnung = Object.fromEntries(
        modellVerwendungen.map((verwendung) => [
          verwendung.id,
          modellKonfiguration.data!.zuordnung[verwendung.id] ?? {
            verbindungId: ersteVerbindung.id,
            modell: ersteVerbindung.modelle[0] ?? "",
          },
        ]),
      ) as Record<ModellVerwendung, ModellAuswahl>;
      return { zuordnung };
    })();
  const modellWerteGueltig = Boolean(
    modellWerte &&
      modellVerwendungen.every((verwendung) => {
        const auswahl = modellWerte.zuordnung[verwendung.id];
        return (
          anbieterVerbindungen.data?.verbindungen.some(
            (verbindung) => verbindung.id === auswahl.verbindungId,
          ) && modellkennungMuster.test(auswahl.modell)
        );
      }),
  );

  const gewaehlterAnbieter =
    anbieterVerbindungen.data?.katalog.find(
      (eintrag) => eintrag.art === anbieterArt,
    ) ?? null;

  function setzeModellVerwendung(
    verwendung: ModellVerwendung,
    aenderung: Partial<ModellAuswahl>,
  ): void {
    if (!modellWerte) return;
    setzeModellGespeichert(false);
    setzeModellEntwurf({
      zuordnung: {
        ...modellWerte.zuordnung,
        [verwendung]: {
          ...modellWerte.zuordnung[verwendung],
          ...aenderung,
        },
      },
    });
  }

  return (
    <EinstellungenHuelle
      aktiv="metarr"
      beschreibung="KI-Anbieter verknüpfen und Arbeitsläufe gezielt auf Modelle verteilen."
    >
      {meldung ? <Fehlerzeile meldung={meldung} /> : null}

      <EinstellungsAbschnitt
        titel="Anbieterverbindungen"
        beschreibung="Nur verknüpfte Zugänge werden angezeigt. API-Schlüssel bleiben verschlüsselt auf dem Server und werden nie an den Browser zurückgegeben."
      >
        {anbieterVerbindungen.isLoading ? (
          <LadenZeile text="Lade Verbindungen …" />
        ) : anbieterVerbindungen.isError ? (
          <Fehlerzeile
            meldung={fehlerMeldung(anbieterVerbindungen.error)}
            onErneut={() => void anbieterVerbindungen.refetch()}
          />
        ) : anbieterVerbindungen.data ? (
          <div className="flex flex-wrap gap-3">
            {anbieterVerbindungen.data.verbindungen.map((verbindung) => (
              <div
                key={verbindung.id}
                className="flex min-w-56 flex-col gap-2.5 bg-wisch p-3"
                data-testid={`ki-verbindung-${verbindung.id}`}
              >
                <div className="flex items-center gap-3">
                <KiAnbieterLogo anbieter={verbindung.anbieter} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <span
                      className={
                        verbindung.letzterTestOk === false
                          ? "size-2 rounded-full bg-amber-500"
                          : "size-2 rounded-full bg-emerald-500"
                      }
                      aria-hidden="true"
                    />
                    <p className="truncate text-sm font-medium text-tinte">
                      {verbindung.name}
                    </p>
                  </div>
                  <p className="mt-0.5 text-xs text-tinte3">
                    {verbindung.quelle === "umgebung"
                      ? "Serverkonfiguration"
                      : `${verbindung.modelle.length} Modelle`}
                  </p>
                </div>
                {anbieterVerbindungen.data.darfBearbeiten ? (
                  <div className="flex shrink-0 gap-1">
                    <button
                      type="button"
                      className="rounded-md p-1.5 text-tinte4 hover:bg-papier hover:text-tinte"
                      onClick={() => anbieterTesten.mutate(verbindung.id)}
                      disabled={anbieterTesten.isPending}
                      aria-label={`${verbindung.name} testen`}
                    >
                      <RefreshCw
                        className={
                          anbieterTesten.isPending
                            ? "size-4 animate-spin"
                            : "size-4"
                        }
                        aria-hidden="true"
                      />
                    </button>
                    {verbindung.quelle !== "umgebung" ? (
                      <button
                        type="button"
                        className="rounded-md p-1.5 text-tinte4 hover:bg-papier hover:text-red-600"
                        onClick={() =>
                          setzeBestaetigeEntfernen({
                            verbindungId: verbindung.id,
                            name: verbindung.name,
                          })
                        }
                        aria-label={`${verbindung.name} entfernen`}
                      >
                        <Trash2 className="size-4" aria-hidden="true" />
                      </button>
                    ) : null}
                  </div>
                ) : null}
                </div>

                {/*
                  Denken und Denkstufe je Verbindung. Nur für gespeicherte
                  Zugänge: Umgebungsverbindungen werden in der
                  Serverkonfiguration eingestellt, nicht hier.

                  Die Vorgabe ist „aus" — der Werkzeugmodus lief bisher
                  ausdrücklich ohne Denken. Wer es einschaltet, probiert aus,
                  ob die erzwungenen Werkzeugaufrufe damit halten.
                */}
                {anbieterVerbindungen.data.darfBearbeiten &&
                verbindung.quelle !== "umgebung" ? (
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-t border-linie2 pt-2.5">
                    <label className="flex cursor-pointer items-center gap-2 text-xs text-tinte2">
                      <input
                        type="checkbox"
                        className="size-3.5 accent-akzent"
                        checked={verbindung.denken === true}
                        onChange={(ereignis) =>
                          denkmodus.mutate({
                            verbindungId: verbindung.id,
                            denken: ereignis.target.checked,
                          })
                        }
                        disabled={denkmodus.isPending}
                      />
                      Denken
                    </label>
                    <label className="flex items-center gap-2 text-xs text-tinte3">
                      Stufe
                      <select
                        className="border border-linie bg-papier px-1.5 py-0.5 font-mono text-[11px] text-tinte2"
                        value={verbindung.denkstufe ?? ""}
                        onChange={(ereignis) =>
                          denkmodus.mutate({
                            verbindungId: verbindung.id,
                            denkstufe:
                              ereignis.target.value === ""
                                ? null
                                : (ereignis.target
                                    .value as (typeof DENKSTUFEN)[number]),
                          })
                        }
                        disabled={denkmodus.isPending}
                        aria-label={`Denkstufe für ${verbindung.name}`}
                      >
                        {/* Leer = Feld nicht senden, nicht „niedrigste Stufe". */}
                        <option value="">ohne</option>
                        {DENKSTUFEN.map((stufe) => (
                          <option key={stufe} value={stufe}>
                            {stufe}
                          </option>
                        ))}
                      </select>
                    </label>
                    {/*
                      Die Antwortlänge gehört neben die Denkstufe, weil beide
                      dasselbe Budget teilen: Bei Reasoning-Modellen zählen die
                      Token fürs Nachdenken mit. Eine Rückfrage scheiterte
                      deshalb an der Grenze, obwohl sie nur 466 Zeichen lang
                      war — das Budget war vor der Antwort aufgebraucht.
                    */}
                    <label className="flex items-center gap-2 text-xs text-tinte3">
                      Antwortlänge
                      <input
                        type="number"
                        min={256}
                        max={200000}
                        step={256}
                        placeholder="Vorgabe"
                        className="w-24 border border-linie bg-papier px-1.5 py-0.5 font-mono text-[11px] tabular-nums text-tinte2"
                        defaultValue={verbindung.maxTokens ?? ""}
                        onBlur={(ereignis) => {
                          const roh = ereignis.target.value.trim();
                          const wert = roh === "" ? null : Number(roh);
                          if (wert !== null && !Number.isSafeInteger(wert)) return;
                          if (wert === (verbindung.maxTokens ?? null)) return;
                          denkmodus.mutate({
                            verbindungId: verbindung.id,
                            maxTokens: wert,
                          });
                        }}
                        disabled={denkmodus.isPending}
                        aria-label={`Antwortlänge in Token für ${verbindung.name}`}
                      />
                    </label>
                  </div>
                ) : null}

                {/*
                  Dasselbe für Umgebungszugänge — mit zwei Unterschieden, die
                  beide Absicht sind.

                  Die Stufen kommen aus den Fähigkeiten der Modelle dieser
                  Verbindung, nicht aus `DENKSTUFEN`. Und angeboten wird nur,
                  was der jeweilige Weg auch entgegennimmt: Der Abo-Zugang geht
                  durch `codex exec`, und die CLI kennt Modell und Denkstufe —
                  `denken` und Antwortlänge kämen dort nie an. Ein Regler ohne
                  Wirkung behauptet eine Einstellung, die es nicht gibt.
                */}
                {anbieterVerbindungen.data.darfBearbeiten &&
                verbindung.quelle === "umgebung" ? (
                  <UmgebungsEinstellungen
                    verbindung={verbindung}
                    laeuft={umgebungsDenkmodus.isPending}
                    aendere={(eingabe) =>
                      umgebungsDenkmodus.mutate({
                        verbindungId: verbindung.id,
                        ...eingabe,
                      })
                    }
                  />
                ) : null}
              </div>
            ))}
            <button
              type="button"
              className="flex min-h-16 min-w-32 items-center justify-center gap-2 border border-dashed border-linie px-4 text-sm font-medium text-tinte2 hover:bg-wisch disabled:cursor-not-allowed disabled:opacity-50"
              onClick={() => setzeAnbieterDialogOffen(true)}
              disabled={!anbieterVerbindungen.data.darfBearbeiten}
              data-testid="ki-anbieter-hinzufuegen"
            >
              <Plus className="size-4" aria-hidden="true" />
              Hinzufügen
            </button>
          </div>
        ) : null}

        {anbieterTesten.data ? (
          <p
            className={
              anbieterTesten.data.erreichbar
                ? "text-sm text-emerald-700 dark:text-emerald-400"
                : "text-sm text-amber-700 dark:text-amber-400"
            }
            role="status"
          >
            {anbieterTesten.data.meldung}
            {anbieterTesten.data.dauerMs !== null
              ? ` (${anbieterTesten.data.dauerMs} ms)`
              : ""}
          </p>
        ) : null}
      </EinstellungsAbschnitt>

      <EinstellungsAbschnitt
        titel="Standard je Arbeitslauf"
        beschreibung="Anbieter und Modell sind je Ablauf frei wählbar. Beim manuellen Start kann der Standard überschrieben werden. Neue Läufe speichern die konkrete Auswahl revisionsfest."
      >
        {modellKonfiguration.isLoading ? (
          <LadenZeile text="Lade Modellkonfiguration …" />
        ) : modellKonfiguration.isError ? (
          <Fehlerzeile
            meldung={fehlerMeldung(modellKonfiguration.error)}
            onErneut={() => void modellKonfiguration.refetch()}
          />
        ) : (anbieterVerbindungen.data?.verbindungen.length ?? 0) === 0 ? (
          <p className="text-sm text-tinte3">
            Verknüpfe zuerst einen Anbieter. Danach lassen sich die Standards
            für alle Arbeitsläufe festlegen.
          </p>
        ) : modellWerte ? (
          <>
            <div className="space-y-2">
              {modellVerwendungen.map((verwendung) => {
                const auswahl = modellWerte.zuordnung[verwendung.id];
                const verbindung =
                  anbieterVerbindungen.data!.verbindungen.find(
                    (eintrag) => eintrag.id === auswahl.verbindungId,
                  ) ?? anbieterVerbindungen.data!.verbindungen[0]!;
                return (
                  <div
                    key={verwendung.id}
                    className="grid gap-3 bg-wisch p-4 sm:grid-cols-[minmax(0,1fr)_minmax(180px,0.7fr)_minmax(200px,0.8fr)] sm:items-center"
                  >
                    <div>
                      <Label htmlFor={`metarr-routing-${verwendung.id}`}>
                        {verwendung.label}
                      </Label>
                      <p className="mt-1 text-xs text-tinte3">
                        {verwendung.beschreibung}
                      </p>
                    </div>
                    <div>
                      <select
                        id={`metarr-routing-${verwendung.id}`}
                        value={auswahl.verbindungId}
                        onChange={(e) => {
                          const naechste =
                            anbieterVerbindungen.data!.verbindungen.find(
                              (eintrag) => eintrag.id === e.target.value,
                            )!;
                          setzeModellVerwendung(verwendung.id, {
                            verbindungId: naechste.id,
                            modell: naechste.modelle[0] ?? "",
                          });
                        }}
                        disabled={!modellKonfiguration.data?.darfBearbeiten}
                        className="w-full border border-linie bg-papier px-3 py-2 text-sm text-tinte outline-none focus:border-tinte disabled:bg-wisch disabled:text-tinte4"
                        data-testid={`metarr-routing-${verwendung.id}`}
                      >
                        {anbieterVerbindungen.data!.verbindungen.map(
                          (eintrag) => (
                            <option key={eintrag.id} value={eintrag.id}>
                              {eintrag.name}
                            </option>
                          ),
                        )}
                      </select>
                    </div>
                    <div>
                      <ModellAuswahl
                        id={`metarr-modell-${verwendung.id}`}
                        modelle={verbindung.modelle}
                        wert={auswahl.modell}
                        onChange={(modell) =>
                          setzeModellVerwendung(verwendung.id, { modell })
                        }
                        disabled={!modellKonfiguration.data?.darfBearbeiten}
                        ariaLabel={`Modell für ${verwendung.label}`}
                        testId={`metarr-modell-${verwendung.id}`}
                      />
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <Button
                onClick={() =>
                  modellKonfigurationSpeichern.mutate(modellWerte)
                }
                disabled={
                  !modellKonfiguration.data?.darfBearbeiten ||
                  !modellWerteGueltig
                }
                isLoading={modellKonfigurationSpeichern.isPending}
                loadingText="Speichere Standards …"
                data-testid="metarr-modellrouting-speichern"
              >
                Standards speichern
              </Button>
              {modellGespeichert ? (
                <p
                  className="text-sm text-emerald-700 dark:text-emerald-400"
                  role="status"
                >
                  Arbeitslauf-Standards gespeichert.
                </p>
              ) : null}
              {!modellKonfiguration.data?.darfBearbeiten ? (
                <p className="text-xs text-tinte3">
                  Nur Personen mit dem Recht Einstellungen verwalten können das
                  Routing ändern.
                </p>
              ) : null}
            </div>
          </>
        ) : null}
      </EinstellungsAbschnitt>

      <Dialog
        open={anbieterDialogOffen}
        onOpenChange={(offen) => {
          setzeAnbieterDialogOffen(offen);
          if (!offen) {
            setzeAnbieterArt(null);
            setzeVerbindungsName("");
            setzeApiKey("");
          }
        }}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {anbieterArt ? "Anbieter verknüpfen" : "Anbieter hinzufügen"}
            </DialogTitle>
            <DialogDescription>
              {anbieterArt
                ? "Cubic prüft den Zugang vor dem Speichern und lädt die verfügbaren Modelle."
                : "Wähle den Zugang, den du mit Cubic verbinden möchtest."}
            </DialogDescription>
          </DialogHeader>

          {anbieterArt ? (
            <div className="space-y-5 py-1">
              <div className="flex items-start gap-3 bg-wisch p-3">
                <KiAnbieterLogo
                  anbieter={
                    anbieterVerbindungen.data!.katalog.find(
                      (eintrag) => eintrag.art === anbieterArt,
                    )!.anbieter
                  }
                />
                <div>
                  <p className="text-sm font-medium text-tinte">
                    {
                      anbieterVerbindungen.data!.katalog.find(
                        (eintrag) => eintrag.art === anbieterArt,
                      )!.name
                    }
                  </p>
                  <p className="text-xs text-tinte3">
                    Zugang über API-Schlüssel
                  </p>
                </div>
              </div>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="ki-verbindungsname">Name der Verbindung</Label>
                  <Input
                    id="ki-verbindungsname"
                    className="mt-1.5"
                    value={verbindungsName}
                    maxLength={200}
                    onChange={(e) => setzeVerbindungsName(e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="ki-api-key">API-Schlüssel</Label>
                  <div className="relative mt-1.5">
                    <KeyRound
                      className="pointer-events-none absolute left-3 top-2.5 size-4 text-tinte4"
                      aria-hidden="true"
                    />
                    <Input
                      id="ki-api-key"
                      type="password"
                      className="pl-9"
                      value={apiKey}
                      maxLength={4_000}
                      autoComplete="new-password"
                      onChange={(e) => setzeApiKey(e.target.value)}
                    />
                  </div>
                  {gewaehlterAnbieter?.schluesselUrl ? (
                    <p className="mt-2 text-xs text-tinte3">
                      <a
                        href={gewaehlterAnbieter.schluesselUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 underline underline-offset-2 hover:text-tinte"
                        data-testid="ki-schluessel-link"
                      >
                        Schlüssel bei {gewaehlterAnbieter.name} erzeugen
                        <ExternalLink className="size-3" aria-hidden="true" />
                      </a>
                    </p>
                  ) : null}
                </div>
              </div>
              {!anbieterVerbindungen.data?.tresorBereit ? (
                <p className="bg-wisch p-3 text-sm text-amber-800 dark:text-amber-300">
                  Der verschlüsselte KI-Tresor ist auf dem Server noch nicht
                  eingerichtet. Setze zuerst CUBIC_KI_TRESORSCHLUESSEL.
                </p>
              ) : null}
            </div>
          ) : (
            <div className="grid gap-3 py-1 sm:grid-cols-2">
              {anbieterVerbindungen.data?.katalog.map((eintrag) => (
                <button
                  key={eintrag.art}
                  type="button"
                  className="group flex min-h-[7rem] flex-col items-start gap-3 border border-linie p-4 text-left transition hover:bg-wisch disabled:opacity-55"
                  /*
                    Ein Abo-Zugang ist verfügbar, aber nicht verknüpfbar: Es
                    gibt keinen Schlüssel einzutragen. Er entsteht auf dem
                    Server über die angemeldete CLI und erscheint dann von
                    selbst in der Liste oben.
                  */
                  disabled={
                    !eintrag.verfuegbar ||
                    eintrag.zugangsart === "subscription"
                  }
                  onClick={() => {
                    setzeAnbieterArt(eintrag.art);
                    setzeVerbindungsName(eintrag.name);
                  }}
                >
                  <div className="flex w-full items-start justify-between gap-2">
                    <KiAnbieterLogo anbieter={eintrag.anbieter} />
                    {!eintrag.verfuegbar ? (
                      <Badge variant="neutral" className="shrink-0">
                        Nicht verfügbar
                      </Badge>
                    ) : eintrag.zugangsart === "subscription" ? (
                      <Badge variant="neutral" className="shrink-0">
                        Über den Server
                      </Badge>
                    ) : (
                      <span className="mt-0.5 text-xs text-tinte4">
                        Verknüpfen →
                      </span>
                    )}
                  </div>
                  <span>
                    <span className="block text-sm font-medium text-tinte">
                      {eintrag.name}
                    </span>
                    <span className="mt-1 block text-xs leading-relaxed text-tinte3">
                      {eintrag.hinweis}
                    </span>
                  </span>
                </button>
              ))}
            </div>
          )}

          <DialogFooter className="mt-4">
            {anbieterArt ? (
              <>
                <Button
                  variant="secondary"
                  onClick={() => setzeAnbieterArt(null)}
                >
                  Zurück
                </Button>
                <Button
                  onClick={() => anbieterVerknuepfen.mutate()}
                  disabled={
                    !anbieterVerbindungen.data?.tresorBereit ||
                    !verbindungsName.trim() ||
                    !apiKey.trim() ||
                    anbieterVerknuepfen.isPending
                  }
                  isLoading={anbieterVerknuepfen.isPending}
                  loadingText="Prüfe und verknüpfe …"
                >
                  Verknüpfen
                </Button>
              </>
            ) : (
              <Button
                variant="secondary"
                onClick={() => setzeAnbieterDialogOffen(false)}
              >
                Schließen
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={bestaetigeEntfernen !== null}
        onOpenChange={(offen) => {
          if (!offen) setzeBestaetigeEntfernen(null);
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Anbieterverbindung entfernen?</DialogTitle>
            <DialogDescription>
              {bestaetigeEntfernen
                ? `„${bestaetigeEntfernen.name}“ wird gelöscht. Neue Läufe können diesen Zugang danach nicht mehr verwenden; gespeicherte Laufdaten bleiben als Historie erhalten.`
                : "Die Verbindung wird gelöscht."}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="secondary"
              onClick={() => setzeBestaetigeEntfernen(null)}
            >
              Abbrechen
            </Button>
            <Button
              onClick={() => {
                if (!bestaetigeEntfernen) return;
                const id = bestaetigeEntfernen.verbindungId;
                setzeBestaetigeEntfernen(null);
                anbieterEntfernen.mutate(id);
              }}
              disabled={anbieterEntfernen.isPending}
            >
              Entfernen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <HausmeisterAbschnitt />
    </EinstellungenHuelle>
  );
}

/**
 * Die Regler eines Zugangs aus der Serverkonfiguration.
 *
 * Zwei Dinge unterscheiden ihn von der Maske der Tresorverbindungen, und
 * beide sind Absicht:
 *
 * Die Stufen kommen aus den Fähigkeiten der Modelle **dieser** Verbindung
 * (`ki-modelle.ts`), nicht aus `DENKSTUFEN`. Das sind zwei verschiedene
 * Leitern — die eine kennt `minimal` und endet bei `high`, die andere beginnt
 * bei `none` und geht bis `max`. Für einen Zugang, dessen Modelle bekannt
 * sind, ist die zweite die richtige.
 *
 * Und angeboten wird nur, was der Weg dorthin auch entgegennimmt. Der
 * Abo-Zugang läuft über `codex exec`; die CLI kennt Modell und Denkstufe,
 * `denken` und Antwortlänge kämen dort nie an. Was dieselbe Regel dazu sagt,
 * sagt sie auch dem Server — sie steht in `@cubic/shared`.
 */
function UmgebungsEinstellungen({
  verbindung,
  laeuft,
  aendere,
}: {
  verbindung: {
    id: string;
    name: string;
    anbieterArt: AnbieterArt;
    zugangsart: "api_schluessel" | "subscription";
    modelle: string[];
    denken: boolean | null;
    denkstufe: string | null;
    maxTokens: number | null;
  };
  laeuft: boolean;
  aendere: (eingabe: {
    denken?: boolean;
    denkstufe?: ReasoningStufe | null;
    maxTokens?: number | null;
  }) => void;
}) {
  const erlaubt = einstellbareFelderFuerUmgebung(verbindung.zugangsart);
  const stufen = [
    ...new Set(
      listeKiModellCapabilities(
        verbindung.anbieterArt,
        verbindung.modelle,
      ).flatMap((capability) => capability.reasoningStufen),
    ),
  ];
  /*
    Kennt die Instanz keine Fähigkeiten zu den Modellen dieser Verbindung,
    gibt es nichts anzubieten. Dann bleibt der Block weg, statt eine leere
    Auswahlliste hinzustellen.
  */
  if (stufen.length === 0 && !erlaubt.denken && !erlaubt.maxTokens) return null;

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-t border-linie2 pt-2.5">
      {erlaubt.denken ? (
        <label className="flex cursor-pointer items-center gap-2 text-xs text-tinte2">
          <input
            type="checkbox"
            className="size-3.5 accent-akzent"
            checked={verbindung.denken === true}
            onChange={(ereignis) => aendere({ denken: ereignis.target.checked })}
            disabled={laeuft}
          />
          Denken
        </label>
      ) : null}
      {erlaubt.denkstufe && stufen.length > 0 ? (
        <label className="flex items-center gap-2 text-xs text-tinte3">
          Stufe
          <select
            className="border border-linie bg-papier px-1.5 py-0.5 font-mono text-[11px] text-tinte2"
            value={verbindung.denkstufe ?? ""}
            onChange={(ereignis) =>
              aendere({
                denkstufe:
                  ereignis.target.value === ""
                    ? null
                    : (ereignis.target.value as ReasoningStufe),
              })
            }
            disabled={laeuft}
            aria-label={`Denkstufe für ${verbindung.name}`}
          >
            {/* Leer = Feld nicht senden. `none` heißt „ausdrücklich ohne". */}
            <option value="">ohne</option>
            {stufen.map((stufe) => (
              <option key={stufe} value={stufe}>
                {stufe}
              </option>
            ))}
          </select>
        </label>
      ) : null}
      {erlaubt.maxTokens ? (
        <label className="flex items-center gap-2 text-xs text-tinte3">
          Antwortlänge
          <input
            type="number"
            min={256}
            max={200000}
            step={256}
            placeholder="Vorgabe"
            className="w-24 border border-linie bg-papier px-1.5 py-0.5 font-mono text-[11px] tabular-nums text-tinte2"
            defaultValue={verbindung.maxTokens ?? ""}
            onBlur={(ereignis) => {
              const roh = ereignis.target.value.trim();
              const wert = roh === "" ? null : Number(roh);
              if (wert !== null && !Number.isSafeInteger(wert)) return;
              if (wert === (verbindung.maxTokens ?? null)) return;
              aendere({ maxTokens: wert });
            }}
            disabled={laeuft}
            aria-label={`Antwortlänge in Token für ${verbindung.name}`}
          />
        </label>
      ) : null}
      {/*
        Damit niemand den fehlenden Regler für einen Fehler hält: Der
        Abo-Zugang bietet weniger an, weil weniger ankommt.
      */}
      {!erlaubt.maxTokens ? (
        <span className="text-[11px] text-tinte4">
          Über die CLI wirken nur Modell und Stufe.
        </span>
      ) : null}
    </div>
  );
}
